import { useCallback, useMemo, useState } from 'react';
import { Popover, Toggle } from '@ets/design-system';
import cx from 'classnames';

import { useDispatch } from '_common/hooks';
import { hexToRgb } from 'assets/colors';

import { openAndUpdateModal } from '_common/modals/ModalsSlice';

import { ColorsMenu } from 'Editor/components';
import { ColorsMenuProps } from 'Editor/components/ColorsMenu/ColorsMenu';

import styles from './ColorPickerToggle.module.scss';
import { EditorDOMUtils } from 'Editor/services/_Common/DOM';
import { getModalContainer } from 'utils';

type ColorPickerToggleProps = {
  color: string;
  onColorChange: (newColor: string) => void;
  disabled?: boolean;
  testId?: string;
};

const ColorPickerToggle = ({ color, onColorChange, disabled, testId }: ColorPickerToggleProps) => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);

  const rbgColor = useMemo(() => {
    if (color) {
      const rbgColor = hexToRgb(color);
      return rbgColor && `rgb(${rbgColor[0]},${rbgColor[1]},${rbgColor[2]})`;
    }
  }, [color]);

  //TODO: Check how this state is populated and clean type accordingly
  const documentColors = useMemo<
    { r?: string | number; g?: string | number; b?: string | number; rgb?: string }[]
  >(() => EditorDOMUtils.getDocumentColors() ?? [], [color]);

  const updateColorCallback = useCallback(
    (newColor: string) => onColorChange(EditorDOMUtils.rgbToHex(newColor) ?? 'transparent'),
    [],
  );

  const handleClearColor = () => onColorChange('transparent');

  const handleColorChange = (newColor: string) =>
    onColorChange(EditorDOMUtils.rgbToHex(newColor) ?? 'transparent');

  const handleOpenColorPickerModalBorderColor: ColorsMenuProps['setOpenColorPickerModal'] = () => {
    dispatch(
      openAndUpdateModal({
        modal: 'ColorPickerModal',
        data: { action: 'updateColor', updateColorCallback },
      }),
    );
  };

  return (
    <>
      <Popover.Root placement="top-end" modal open={isOpen} onOpenChange={() => setIsOpen(!isOpen)}>
        <Popover.Trigger>
          <Toggle
            onToggle={() => setIsOpen(!isOpen)}
            variant="standard"
            margin="0 0 3rem 0"
            testId={`${testId}`}
            disabled={disabled}
          >
            <div
              className={cx(styles.box, { [styles.transparent]: color === 'transparent' })}
              style={{
                background: color,
              }}
            />
          </Toggle>
        </Popover.Trigger>
        <Popover.Content container={getModalContainer()} testId={`${testId}`}>
          <ColorsMenu
            selected={rbgColor}
            documentColors={documentColors}
            toggle={() => setIsOpen(false)}
            changeBackgroundColor={handleColorChange}
            clearBackgroundColor={handleClearColor}
            setOpenColorPickerModal={handleOpenColorPickerModalBorderColor}
          />
        </Popover.Content>
      </Popover.Root>
    </>
  );
};

export default ColorPickerToggle;
